/**
 * OrganizationLandingPage — the platform homepage.
 * Which sections appear, in what order and with what wording is set in
 * Admin → Homepage (`/api/public/landing`). Everything else is live data.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { eventAPI, churchesAPI, cmsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, ArrowUpRight, CalendarDays, MapPin, Church, GraduationCap,
  BookOpen, Music, Radio, Heart, ShieldCheck, Building2, Ticket,
} from 'lucide-react';
import { FwmHeader, FwmFooter } from '@/components/fwm/FwmShell';
import { FwmHero, FwmStatStrip } from '@/components/fwm/FwmHero';
import { useHero } from '@/components/fwm/useHero';
import { FWM_IMAGERY, resolveImage } from '@/components/fwm/imagery';
import { BannerImage } from '@/components/fwm/BannerImage';
import { campaignProgress, money } from '@/components/fwm/campaignProgress';
import { useSiteBrand } from '@/lib/useSiteBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useReveal = () => {
  const [node, setNode] = useState(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!node || shown) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.12 });
    io.observe(node);
    return () => io.disconnect();
  }, [node, shown]);
  return [setNode, shown];
};

const Band = ({ id, children, className = '', testId }) => {
  const [ref, shown] = useReveal();
  return (
    <section id={id} ref={ref} data-testid={testId}
             className={`px-6 lg:px-10 py-24 lg:py-32 ex-reveal ${shown ? 'is-in' : ''} ${className}`}>
      <div className="mx-auto max-w-[1180px]">{children}</div>
    </section>
  );
};

const Head = ({ kicker, heading, blurb, accent, cta, onCta }) => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
    <div className="max-w-2xl">
      {kicker && (
        <p className="text-[11px] font-semibold tracking-[0.22em] uppercase mb-4"
           style={{ color: accent }}>{kicker}</p>
      )}
      <h2 className="ex-display text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--ex-ink)] leading-[1.15]">
        {heading}
      </h2>
      {blurb && <p className="mt-5 text-[color:var(--ex-body)] leading-relaxed">{blurb}</p>}
    </div>
    {cta && (
      <button onClick={onCta} data-testid="band-cta"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--ex-body)] hover:text-[color:var(--ex-ink)] transition-colors shrink-0">
        {cta} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </button>
    )}
  </div>
);

const Counter = ({ value, run }) => {
  const target = Number(String(value ?? '').replace(/[^0-9.]/g, '')) || 0;
  // Start at the real figure so first paint — and any crawler or link
  // preview — sees the true number. The roll-up is a flourish on top.
  const [n, setN] = useState(target);
  useEffect(() => { setN(target); }, [target]);
  useEffect(() => {
    if (!run || !target) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return undefined;
    let frame;
    const from = target * 0.7;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 900);
      setN(from + (target - from) * (1 - (1 - p) ** 3));
      if (p < 1) frame = requestAnimationFrame(tick);
      else setN(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [run, target]);
  return <>{Math.round(n).toLocaleString()}</>;
};

const Rule = () => <div className="h-px w-full bg-[color:var(--ex-line)]" />;

const StatsStrip = ({ tiles }) => {
  if (!tiles.length) return null;
  return (
    <div data-testid="landing-stats">
      <FwmStatStrip testId="landing-stat-strip"
                    stats={tiles.map(([label, value]) => [
                      Math.round(Number(value || 0)).toLocaleString(), label])} />
    </div>
  );
};

const OrganizationLandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const id = 'ex-landing-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,500;6..72,600&family=Inter+Tight:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  const { data: landing } = useQuery({
    queryKey: ['public-landing'],
    queryFn: () => fetch(`${API_URL}/api/public/landing`).then((r) => r.json()),
    staleTime: 60000,
  });
  const { data: events } = useQuery({
    queryKey: ['events'], queryFn: () => eventAPI.list().then((r) => r.data),
  });
  const { data: churches } = useQuery({
    queryKey: ['public-churches'], queryFn: () => churchesAPI.list().then((r) => r.data),
    staleTime: 300000,
  });
  const { data: pages } = useQuery({
    queryKey: ['public-pages'], queryFn: () => cmsAPI.listPages().then((r) => r.data),
    staleTime: 300000,
  });
  const { data: campaigns } = useQuery({
    queryKey: ['public-campaigns'],
    queryFn: () => fetch(`${API_URL}/api/campaigns`).then((r) => r.json()).catch(() => []),
    staleTime: 300000,
  });
  const { data: summary } = useQuery({
    queryKey: ['public-platform-summary'],
    queryFn: () => fetch(`${API_URL}/api/public/platform-summary`).then((r) => r.json()),
    staleTime: 60000,
  });
  const { accent: brandAccent } = useSiteBrand();

  const theme = landing?.theme || {};
  const modeOverride = new URLSearchParams(window.location.search).get('mode');
  const dark = (modeOverride || theme.mode) === 'dark';
  const preset = dark ? { accent: '#D8B25A', surface: '#0B0C0F' }
                      : { accent: '#B4881F', surface: '#FAF7F1' };
  // Site Appearance → Brand is the single source of the house accent; the
  // dark/light preset is the fallback (and `?mode=` previews use the preset).
  const accent = (modeOverride ? preset.accent : brandAccent)
    || theme.accent || preset.accent;
  const surface = (modeOverride ? preset.surface : theme.surface) || preset.surface;
  const sections = landing?.sections || [];
  const on = (id) => sections.some((s) => s.id === id);
  const props = (id) => sections.find((s) => s.id === id)?.props || {};

  const upcoming = (events || []).filter((e) => new Date(e.start_date) > new Date()).slice(0, 3);
  const liveCampaigns = (Array.isArray(campaigns) ? campaigns : []).slice(0, 3);
  const churchList = (Array.isArray(churches) ? churches : []).slice(0, 6);
  const publishedPages = (Array.isArray(pages) ? pages : []).filter((p) => p.is_published !== false).slice(0, 6);

  const hero = props('hero');
  const featuredProps = props('featured');
  const eventList = Array.isArray(events) ? events : [];
  const featuredEvent = featuredProps.event_id
    ? eventList.find((e) => e.id === featuredProps.event_id || e.slug === featuredProps.event_id)
    : (upcoming[0] || [...eventList].sort(
        (a, b) => new Date(b.start_date) - new Date(a.start_date))[0]);
  // Above the fold visitors should meet people, not texture — fall back to
  // real congregation photography when no hero asset is set in the admin.
  const heroVisual = resolveImage(hero.hero_image) || FWM_IMAGERY.heroHome;
  const pageHero = useHero('home', { image_url: heroVisual });
  const featuredArt = featuredEvent && (
    resolveImage(featuredEvent.branding?.hero_image_url)
    || resolveImage(featuredEvent.banner_url)
    || resolveImage(featuredEvent.cover_image_url)
    || FWM_IMAGERY.gathering);

  return (
    <div className="min-h-screen ex-page text-[color:var(--ex-ink)]" data-testid="landing-page"
         data-mode={dark ? 'dark' : 'light'}
         style={{ backgroundColor: surface }}>
      <style>{`
        .ex-page { font-family: 'Inter Tight', 'DM Sans', ui-sans-serif, system-ui, sans-serif;
                   --ex-accent: ${accent};
                   --fwm-accent: ${accent};
                   --fwm-ink: var(--ex-ink);
                   --fwm-body: var(--ex-body);
                   --fwm-muted: var(--ex-muted);
                   --fwm-line: var(--ex-line);
                   --fwm-strong: var(--ex-strong);
                   --fwm-card: var(--ex-card);
                   --fwm-hover: var(--ex-hover);
                   --fwm-nav: var(--ex-nav);
                   ${dark ? `
                   --ex-ink: #FFFFFF;
                   --ex-body: rgba(255,255,255,.58);
                   --ex-muted: rgba(255,255,255,.42);
                   --ex-line: rgba(255,255,255,.09);
                   --ex-strong: rgba(255,255,255,.22);
                   --ex-card: #101216;
                   --ex-hover: rgba(255,255,255,.04);
                   --ex-nav: rgba(11,12,15,.72);
                   --ex-grain: rgba(255,255,255,.05);` : `
                   --ex-ink: #14110F;
                   --ex-body: rgba(20,17,15,.62);
                   --ex-muted: rgba(20,17,15,.45);
                   --ex-line: rgba(0,0,0,.08);
                   --ex-strong: rgba(0,0,0,.22);
                   --ex-card: #FFFFFF;
                   --ex-hover: rgba(0,0,0,.03);
                   --ex-nav: rgba(250,247,241,.82);
                   --ex-grain: rgba(0,0,0,.06);`} }
        .ex-display { font-family: ${theme.serif_headings === false
          ? "'Inter Tight', sans-serif" : "'Newsreader', Georgia, serif"}; }
        .ex-reveal { opacity: 0; transform: translateY(26px);
          transition: opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1); }
        .ex-reveal.is-in { opacity: 1; transform: none; }
        .ex-grain:before { content:''; position:absolute; inset:0; pointer-events:none; opacity:.35;
          background-image: radial-gradient(var(--ex-grain) 1px, transparent 1px);
          background-size: 3px 3px; }
        @media (prefers-reduced-motion: reduce) {
          .ex-reveal { opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      {/* ── Top bar (shared across every public section) ── */}
      <FwmHeader ctaLabel={hero.cta_label || 'Explore events'}
                 ctaLink={hero.cta_link || '/events'} overDark />

      {/* ── Hero — full-bleed ministry photography or video under a scrim ── */}
      <FwmHero
        testId="landing-hero"
        image={pageHero.image_url || heroVisual}
        video={pageHero.video_url || hero.hero_video || ''}
        mediaPriority={(pageHero.video_url || hero.hero_video)
          ? pageHero.media_priority : 'image'}
        eyebrow={pageHero.eyebrow || hero.kicker}
        title={pageHero.title || hero.heading}
        accent={pageHero.accent}
        blurb={pageHero.blurb || hero.blurb}
        actions={(
          <>
            <button className="fwm-btn fwm-btn-lg" data-testid="hero-cta-primary"
                    onClick={() => navigate(hero.cta_link || '/events')}>
              {hero.cta_label || 'Explore'} <ArrowRight className="h-4 w-4" />
            </button>
            {hero.secondary_label && (
              <button className="fwm-btn-ondark" data-testid="hero-cta-secondary"
                      onClick={() => navigate(hero.secondary_link || '/churches')}>
                {hero.secondary_label}
              </button>
            )}
          </>
        )}
      />

      {/* ── Live figures, lifted over the photograph ── */}
      {on('stats') && (
        <StatsStrip tiles={[['Live events', summary?.events?.active],
                            ['Churches', summary?.churches?.total],
                            ['Campaigns', summary?.campaigns?.active],
                            ['Schools', summary?.schools?.active]]
          .filter(([, v]) => v !== undefined && v !== null)} />
      )}

      {/* ── Featured event ── */}
      {on('featured') && featuredEvent && (
        <section data-testid="landing-featured"
                 className="border-y border-[color:var(--ex-line)] mt-20"
                 style={{ backgroundColor: 'var(--ex-card)' }}>
          <div className="mx-auto max-w-[1180px] px-6 lg:px-10 py-12 lg:py-16
                          flex flex-col lg:flex-row lg:items-center gap-10">
            <BannerImage src={featuredArt} alt={featuredEvent.name}
                         label={featuredEvent.name} icon={CalendarDays}
                         testId="landing-featured-image"
                         className="w-full lg:w-[380px] h-[200px] object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              {featuredProps.kicker && (
                <p className="text-[11px] font-semibold tracking-[0.24em] uppercase"
                   style={{ color: accent }} data-testid="landing-featured-kicker">
                  {featuredProps.kicker}
                </p>
              )}
              <h2 className="ex-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight"
                  data-testid="landing-featured-name">
                {featuredEvent.name}
              </h2>
              <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[color:var(--ex-muted)]">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {new Date(featuredEvent.start_date).toLocaleDateString(undefined,
                    { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                {featuredEvent.venue_name && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {featuredEvent.venue_name}
                  </span>
                )}
              </p>
            </div>
            <Button size="lg" data-testid="landing-featured-cta"
                    onClick={() => navigate(`/event/${featuredEvent.slug || featuredEvent.id}`)}
                    className="rounded-none h-12 px-8 font-semibold text-black hover:brightness-110 shrink-0"
                    style={{ backgroundColor: accent }}>
              {featuredProps.cta_label || 'Register now'} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </section>
      )}

      {/* ── Events ── */}
      {on('events') && (
        <Band id="events" testId="landing-events">
          <Head {...props('events')} accent={accent}
                cta={props('events').cta_label}
                onCta={() => navigate(props('events').cta_link || '/events')} />
          {upcoming.length === 0 ? (
            <p className="text-[color:var(--ex-muted)] text-sm" data-testid="landing-events-empty">
              Nothing is on the calendar just yet — new events are published here first.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--ex-line)]">
              {upcoming.map((e) => (
                <button key={e.id} data-testid={`landing-event-${e.id}`}
                        onClick={() => navigate(`/event/${e.slug || e.id}`)}
                        className="group text-left bg-[color:var(--ex-card)] p-8 hover:bg-[color:var(--ex-hover)] transition-colors">
                  <p className="text-[11px] font-mono text-[color:var(--ex-muted)]">
                    {new Date(e.start_date).toLocaleDateString(undefined,
                      { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <h3 className="ex-display mt-4 text-xl font-semibold leading-snug">{e.name}</h3>
                  {e.venue_name && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-[color:var(--ex-muted)]">
                      <MapPin className="h-3 w-3" /> {e.venue_name}
                    </p>
                  )}
                  <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold"
                        style={{ color: accent }}>
                    Register <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </Band>
      )}

      {/* ── Campaigns ── */}
      {on('campaigns') && (
        <Band id="giving" testId="landing-campaigns" className="border-t border-[color:var(--ex-line)]">
          <Head {...props('campaigns')} accent={accent}
                cta={props('campaigns').cta_label}
                onCta={() => navigate(props('campaigns').cta_link || '/giving')} />
          {liveCampaigns.length === 0 ? (
            <p className="text-[color:var(--ex-muted)] text-sm" data-testid="landing-campaigns-empty">
              No campaign is running at the moment.
            </p>
          ) : (
            <div className="space-y-px bg-[color:var(--ex-line)]">
              {liveCampaigns.map((c) => {
                const p = campaignProgress(c);
                return (
                  <button key={c.id} data-testid={`landing-campaign-${c.id}`}
                          onClick={() => navigate(`/campaigns/${c.slug || c.id}`)}
                          className="group w-full text-left bg-[color:var(--ex-card)] px-8 py-7 hover:bg-[color:var(--ex-hover)] transition-colors">
                    <div className="flex flex-wrap items-baseline justify-between gap-4">
                      <h3 className="ex-display text-lg font-semibold">{c.name || c.title}</h3>
                      {p.needsTarget ? (
                        <span className="text-xs font-semibold uppercase tracking-[0.14em]"
                              style={{ color: accent }}
                              data-testid={`landing-campaign-no-target-${c.id}`}>
                          Target not set — add one in admin
                        </span>
                      ) : (
                        <span className="text-sm text-[color:var(--ex-muted)] tabular-nums"
                              data-testid={`landing-campaign-progress-${c.id}`}>
                          {money(p.raised, p.currency)} raised · {p.label} of {money(p.target, p.currency)}
                        </span>
                      )}
                    </div>
                    <div className="mt-4 h-[3px] w-full bg-[color:var(--ex-line)]">
                      <div className="h-full transition-all duration-700"
                           style={{ width: `${p.pct}%`, backgroundColor: accent }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Band>
      )}

      {/* ── Bible & media ── */}
      {on('media') && (
        <Band id="media" testId="landing-media" className="border-t border-[color:var(--ex-line)]">
          <Head {...props('media')} accent={accent} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--ex-line)]">
            {[['Bible', 'Read, plan and journal', BookOpen, props('media').bible_link || '/bible'],
              ['Sermons', 'Teaching on demand', Radio, props('media').sermons_link || '/media'],
              ['Worship', 'Songs and sets', Music, props('media').worship_link || '/portal'],
              ['Giving', 'Give from anywhere', Heart, props('media').giving_link || '/giving']]
              .map(([title, blurb, Icon, href]) => (
                <button key={title} onClick={() => navigate(href)}
                        data-testid={`landing-media-${title.toLowerCase()}`}
                        className="group text-left bg-[color:var(--ex-card)] p-8 hover:bg-[color:var(--ex-hover)] transition-colors">
                  <Icon className="h-5 w-5" style={{ color: accent }} />
                  <h3 className="ex-display mt-5 text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--ex-muted)]">{blurb}</p>
                </button>
              ))}
          </div>
        </Band>
      )}

      {/* ── Churches ── */}
      {on('churches') && (
        <Band id="churches" testId="landing-churches" className="border-t border-[color:var(--ex-line)]">
          <Head {...props('churches')} accent={accent}
                cta={props('churches').cta_label}
                onCta={() => navigate(props('churches').cta_link || '/churches')} />
          {churchList.length === 0 ? (
            <p className="text-[color:var(--ex-muted)] text-sm" data-testid="landing-churches-empty">
              The directory is being prepared.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-8">
              {churchList.map((c) => (
                <div key={c.id} data-testid={`landing-church-${c.id}`} className="group">
                  <Rule />
                  <button onClick={() => navigate(`/church/${c.slug || c.id}`)}
                          className="w-full text-left pt-5">
                    <p className="ex-display text-lg font-semibold group-hover:opacity-80 transition-opacity">
                      {c.name}
                    </p>
                    <p className="mt-1.5 text-xs text-[color:var(--ex-muted)]">
                      {[c.city, c.country].filter(Boolean).join(', ') || 'Location coming soon'}
                    </p>
                  </button>
                </div>
              ))}
            </div>
          )}
        </Band>
      )}

      {/* ── For schools ── */}
      {on('schools') && (
        <Band id="schools" testId="landing-schools" className="border-t border-[color:var(--ex-line)]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Head {...props('schools')} accent={accent} />
              <Button size="lg" data-testid="landing-schools-cta"
                      onClick={() => navigate(props('schools').cta_link || '/schools')}
                      className="rounded-none h-12 px-8 font-semibold text-black hover:brightness-110"
                      style={{ backgroundColor: accent }}>
                {props('schools').cta_label || 'Learn more'} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[color:var(--ex-line)] p-8">
                <GraduationCap className="h-6 w-6" style={{ color: accent }} />
                <ul className="mt-6 space-y-4 text-sm text-[color:var(--ex-body)]">
                  {['Admissions and enrolment end to end',
                    'Timetables, results and report cards',
                    'Fees that reconcile to the ledger',
                    'Faculties, deans and campus scoping'].map((line) => (
                      <li key={line} className="flex gap-3">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full"
                              style={{ backgroundColor: accent }} />
                        {line}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </Band>
      )}

      {/* ── Assurance ── */}
      {on('assurance') && (
        <Band testId="landing-assurance" className="border-t border-[color:var(--ex-line)]">
          <Head {...props('assurance')} accent={accent} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {(props('assurance').points || []).map((point, idx) => (
              <div key={point} data-testid={`landing-assurance-${idx}`}>
                <Rule />
                <ShieldCheck className="h-4 w-4 mt-6" style={{ color: accent }} />
                <p className="mt-4 text-[color:var(--ex-body)] leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </Band>
      )}

      {/* ── Custom pages ── */}
      {on('pages') && publishedPages.length > 0 && (
        <Band testId="landing-pages" className="border-t border-[color:var(--ex-line)]">
          <Head {...props('pages')} accent={accent} />
          <div className="flex flex-wrap gap-3">
            {publishedPages.map((p) => (
              <Link key={p.id || p.slug} to={`/page/${p.slug}`}
                    data-testid={`landing-page-link-${p.slug}`}
                    className="border border-[color:var(--ex-line)] px-5 py-2.5 text-sm text-[color:var(--ex-body)]
                               hover:text-[color:var(--ex-ink)] hover:border-[color:var(--ex-strong)] transition-colors">
                {p.title}
              </Link>
            ))}
          </div>
        </Band>
      )}

      {/* ── Closing CTA ── */}
      {on('cta') && (
        <Band testId="landing-cta" className="border-t border-[color:var(--ex-line)]">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
            <div>
              <h2 className="ex-display text-3xl sm:text-4xl font-semibold tracking-tight">
                {props('cta').heading}
              </h2>
              <p className="mt-4 text-[color:var(--ex-body)]">{props('cta').blurb}</p>
            </div>
            <Button size="lg" data-testid="landing-final-cta"
                    onClick={() => navigate(props('cta').cta_link || '/contact')}
                    className="rounded-none h-12 px-8 font-semibold text-black hover:brightness-110 shrink-0"
                    style={{ backgroundColor: accent }}>
              {props('cta').cta_label || 'Get in touch'} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Band>
      )}

      {/* ── Footer (shared) ── */}
      <FwmFooter />
    </div>
  );
};

export default OrganizationLandingPage;
